import { ApiActions, apiState } from '@orientaction/api-actions';
import { useLanguage, useOAMatch, useProfileMenu } from '@orientaction/hooks';
import { userService } from '@orientaction/services';
import { GB_PREFIX, getFromLS, isAdmin, OA_MATCH_SERVICE_IDS, RATTACHED_BENEFICIARY_SERVICE_IDS, updateUserInfoInLS, userHasAllowedService, userHasAnyService, userHasService } from '@orientaction/utils';
import { kebabCase } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { getEbookProgress, getGameProgress } from '../../MyResources/components/ResourceCard';
import { getMyBook } from '../../../../services/BookTemplate.service';
import * as MajorTest from '../../../../services/MajorTest.service';
import { getMe } from '../../../../services/user.service';
import { apiServiceNative } from '../../../../utils/fetchNative';

const MAJOR_RESULT_KEYS = [
  'meaningTestLastResult',
  'needsTestLastResult',
  'needsProTestLastResult',
  'preferenceTestLastResult',
  'harmonyTestLastResult',
  'studyTestLastResult',
  'preferenceJeuneLastResult',
] as const;

export type PendingTest = {
  id: number | string;
  title: string;
  time?: string;
};

export type ResourceCounts = {
  tests: number | null;
  games: number | null;
  ebooks: number | null;
};

export type HeroStats = {
  completedTests: number;
  completedGames: number;
  readEbooks: number;
};

const parseUser = () => {
  try {
    return JSON.parse(getFromLS('userInfos') || '{}');
  } catch {
    return {};
  }
};

export const useHomePage = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { language } = useLanguage();
  const token = getFromLS('userToken') || '';
  const user = useMemo(parseUser, []);
  const { tabLayout } = useSelector(apiState);
  const { logout } = useProfileMenu({ user });
  const { verifyUser, signupToOAMatch } = useOAMatch();

  const activateMatch = getFromLS('activateMatch') === 'true';
  const disableMatch = process.env.REACT_APP_DISABLE_MATCH === 'true' && !activateMatch;

  const [testsToDo, setTestsToDo] = useState<PendingTest[]>([]);
  const [openPendingTestsModal, setOpenPendingTestsModal] = useState(false);
  const [openRecrutementModal, setOpenRecrutementModal] = useState(false);
  const [openCandidatureModal, setOpenCandidatureModal] = useState(false);
  const [openDeclineCandidatureModal, setOpenDeclineCandidatureModal] = useState(false);
  const [openEnglishModal, setOpenEnglishModal] = useState(false);
  const [openDialogOAMatch, setOpenDialogOAMatch] = useState(false);
  const [loadingCandidature, setLoadingCandidature] = useState(false);
  const [loadingOAMatch, setLoadingOAMatch] = useState(false);
  const [candidature, setCandidature] = useState<any>();
  const [hasCandidature, setHasCandidature] = useState(false);
  const [idEbook, setIdEbook] = useState<number>();
  const [resourceCounts, setResourceCounts] = useState<ResourceCounts>({
    tests: null,
    games: null,
    ebooks: null,
  });
  const [heroStats, setHeroStats] = useState<HeroStats>({
    completedTests: 0,
    completedGames: 0,
    readEbooks: 0,
  });

  const isRattached = useMemo(() => Boolean(user?.rattachement), [user?.rattachement]);

  const serviceName = useMemo(
    () => user?.service?.name || 'Bilan de compétences',
    [user?.service?.name],
  );
  const [textPoppinOAMatch, setTextPoppinOAMatch] = useState({
    title: '',
    text: 'Voulez-vous rejoindre Match le réseau social qui s’adapte à votre personnalité ?',
    btnClose: 'Pas maintenant',
    btnAccess: 'Rejoindre',
  });

  const urlOAMatch = useCallback(() => {
    return `${process.env.REACT_APP_OA_MATCH_URL}/connexion/#token=${token}`;
  }, [token]);

  const canAccessOaMatch = useMemo(
    () => userHasAnyService(user, OA_MATCH_SERVICE_IDS) || !user?.rattachement,
    [user],
  );

  const showJobOffers = useMemo(
    () => userHasAllowedService(user, 4),
    [user],
  );

  const openJobOffersAdmin = useCallback(() => {
    history.push('/admin/emploi/board');
  }, [history]);

  const redirect = useCallback(
    (index: number, resourceTab?: 'seriousgame' | 'ebook') => {
      dispatch(ApiActions.setTabLayout(index as any));

      switch (index) {
        case 1:
          history.push('/user/tests');
          break;
        case 2:
          history.push(language === GB_PREFIX ? '/en/my-results' : '/mes-resultats');
          break;
        case 3:
          if (resourceTab) {
            sessionStorage.setItem('redirect', JSON.stringify([resourceTab]));
          }
          history.push(language === GB_PREFIX ? '/en/my-resources' : '/mes-ressources');
          break;
        case 4:
          history.push(
            language === GB_PREFIX
              ? `/en/my-compass/${new Date().getFullYear()}`
              : `/ma-boussole/${new Date().getFullYear()}`,
          );
          break;
        default:
          break;
      }
    },
    [dispatch, history, language],
  );

  const goToTest = useCallback(
    (idTest: number | string, title: string) => {
      history.push(`/test/${idTest}/${kebabCase(title)}`);
    },
    [history],
  );

  const getEbookRecrutement = useCallback(async () => {
    try {
      const response = await apiServiceNative.get(`/api/job/ebook-recrutement-user/${user.id}`);
      setIdEbook(response?.data?.id);
    } catch (error) {
      console.error(error);
    }
  }, [user.id]);

  const checkCandidature = useCallback(async () => {
    const currentUser = parseUser();
    const { data, hasRequiredInfo } = await apiServiceNative.get(
      `/api/job/find-check-candidature/${currentUser.id}`,
    );
    setOpenRecrutementModal((userHasService(currentUser, 4) || data) && hasRequiredInfo);
    if (data) {
      setHasCandidature(true);
      getEbookRecrutement();
    }
  }, [getEbookRecrutement]);

  const findCandidature = useCallback(async () => {
    const currentUser = parseUser();
    const seekResp = await apiServiceNative.get(
      `/api/job/find-invitation-candidature/${currentUser.id}`,
    );
    if (seekResp.data) {
      setOpenCandidatureModal(true);
      setCandidature(seekResp.data);
    }
  }, []);

  const fetchResourceCounts = useCallback(async () => {
    const currentUser = parseUser();
    try {
      const shouldIncludeLegacyEbooks = Boolean(
        currentUser?.rattachement &&
          userHasAnyService(currentUser, RATTACHED_BENEFICIARY_SERVICE_IDS),
      );
      const [
        gamesResponse,
        ebooksResponse,
        legacyEbooksResponse,
        recruitmentEbookResponse,
        candidatureResponse,
      ] = await Promise.all([
        apiServiceNative.get('/api/game-list-user'),
        apiServiceNative.get('/api/ebook-list-user'),
        shouldIncludeLegacyEbooks
          ? getMyBook(token, currentUser.id, '', 0)
          : Promise.resolve(null),
        apiServiceNative.get(`/api/job/ebook-recrutement-user/${currentUser.id}`),
        apiServiceNative.get(`/api/job/find-check-candidature/${currentUser.id}`),
      ]);
      const legacyEbooks = legacyEbooksResponse?.data?.list || [];
      const recruitmentEbook = recruitmentEbookResponse?.data;
      const shouldIncludeRecruitmentEbook =
        recruitmentEbook && (candidatureResponse?.data || userHasService(currentUser, 6));

      setResourceCounts(prev => ({
        ...prev,
        games: Array.isArray(gamesResponse) ? gamesResponse.length : 0,
        ebooks:
          (Array.isArray(ebooksResponse) ? ebooksResponse.length : 0) +
          legacyEbooks.length +
          Number(Boolean(shouldIncludeRecruitmentEbook)),
      }));
    } catch (error) {
      console.error(error);
    }
  }, [token]);

  const fetchHeroStats = useCallback(async () => {
    const currentUser = parseUser();
    if (!token || !currentUser?.id || !currentUser?.rattachement) {
      return;
    }

    try {
      const shouldIncludeLegacyEbooks = userHasAnyService(
        currentUser,
        RATTACHED_BENEFICIARY_SERVICE_IDS,
      );
      const [
        gamesResponse,
        ebooksResponse,
        legacyEbooksResponse,
        recruitmentEbookResponse,
        candidatureResponse,
        latestResultsResponse,
      ] = await Promise.all([
        apiServiceNative.get('/api/game-list-user'),
        apiServiceNative.get('/api/ebook-list-user'),
        shouldIncludeLegacyEbooks ? getMyBook(token, currentUser.id, '', 0) : Promise.resolve(null),
        apiServiceNative.get(`/api/job/ebook-recrutement-user/${currentUser.id}`),
        apiServiceNative.get(`/api/job/find-check-candidature/${currentUser.id}`),
        MajorTest.getUserAllTestLatestResult(token, currentUser.id),
      ]);

      const games = Array.isArray(gamesResponse) ? gamesResponse : [];
      const ebooks = Array.isArray(ebooksResponse) ? ebooksResponse : [];
      const legacyEbooks = legacyEbooksResponse?.data?.list || [];
      const recruitmentEbook = recruitmentEbookResponse?.data;
      const shouldIncludeRecruitmentEbook =
        recruitmentEbook && (candidatureResponse?.data || userHasService(currentUser, 6));
      const latestResults = latestResultsResponse?.data?.data || {};

      const completedMajorTests = MAJOR_RESULT_KEYS.filter(key => latestResults?.[key]).length;
      const completedMinorTests = (latestResults?.customTestResults || []).length;

      setHeroStats({
        completedTests: completedMajorTests + completedMinorTests,
        completedGames: games.filter((game: any) => getGameProgress(game).isCompleted).length,
        readEbooks: ebooks
          .concat(legacyEbooks)
          .concat(shouldIncludeRecruitmentEbook ? recruitmentEbook : [])
          .filter((book: any) =>
            getEbookProgress(
              Boolean(book?.date_consultation || book?.attributes?.dateConsultation),
            ).isCompleted,
          ).length,
      });
    } catch (error) {
      console.error(error);
    }
  }, [token]);

  useEffect(() => {
    const bootstrap = async () => {
      const dataMe = await getMe(token);
      if (dataMe?.data) {
        updateUserInfoInLS({
          service: dataMe.data.service,
          services: dataMe.data.services,
          allowed_services: dataMe.data.allowed_services,
          rattachement: dataMe.data.rattachement,
        });

        setTestsToDo(dataMe.data.tests_to_do || []);
        setResourceCounts(prev => ({
          ...prev,
          tests: (dataMe.data.tests_to_do?.length || 0) + 7,
        }));

        const refreshedUser = parseUser();
        if (!refreshedUser?.secondConnexion) {
          if (dataMe.data.isMyFirstTest) {
            setOpenPendingTestsModal(
              Boolean(dataMe.data.tests_to_do && dataMe.data.tests_to_do.length > 0),
            );
          }
          updateUserInfoInLS({ secondConnexion: true });
          setOpenDialogOAMatch(
            (userHasAnyService(refreshedUser, OA_MATCH_SERVICE_IDS) ||
              !refreshedUser?.rattachement) &&
              (!refreshedUser?.statusOAMatchModal || refreshedUser?.statusOAMatchModal === 'none'),
          );
        }
      }

      await Promise.all([
        findCandidature(),
        checkCandidature(),
        fetchResourceCounts(),
        fetchHeroStats(),
      ]);
    };

    if (token) {
      bootstrap();
    }
  }, [checkCandidature, fetchHeroStats, fetchResourceCounts, findCandidature, token]);

  const updateFirstConnexion = useCallback(async () => {
    try {
      updateUserInfoInLS({ isMyFirstTest: true });
      await userService.modifyUser({
        token,
        isMyFirstTest: true,
        id: user?.id,
      });
    } catch (error) {
      console.error(error);
    }
  }, [token, user?.id]);

  useEffect(() => {
    const checkFirstConnexion = async () => {
      const dataMe = await getMe(token);
      if (
        !dataMe.data.isMyFirstTest &&
        (dataMe.data.beneficiaryType === 'rattached' || dataMe.data.beneficiaryType === 'inCard') &&
        !isAdmin(dataMe.data)
      ) {
        await updateFirstConnexion();
      }
    };

    if (tabLayout === 0 && token) {
      checkFirstConnexion();
    }
  }, [tabLayout, token, updateFirstConnexion]);

  const updateViewOAMatchModal = useCallback(
    async (status: string) => {
      try {
        updateUserInfoInLS({ statusOAMatchModal: status });
        await userService.modifyUser({
          token,
          statusOAMatchModal: status,
          id: user?.id,
        });
      } catch (error) {
        console.error(error);
      }
    },
    [token, user?.id],
  );

  const addSubNetwork = useCallback(async (idUser: number) => {
    try {
      await apiServiceNative.get(`/api/create-update-value-personality-subnetwork?userId=${idUser}`);
      await apiServiceNative.get(`/api/create-update-value-meaning-subnetwork?userId=${idUser}`);
    } catch (error) {
      console.error(error);
    }
  }, []);

  const closeModalOA = useCallback(() => {
    setOpenDialogOAMatch(false);
    updateViewOAMatchModal('refused');
  }, [updateViewOAMatchModal]);

  const closeAndRedirect = useCallback(() => {
    window.open(urlOAMatch(), '_blank');
    setOpenDialogOAMatch(false);
  }, [urlOAMatch]);

  const redirectToOAMatch = useCallback(async () => {
    setLoadingOAMatch(true);
    try {
      const userExist = await verifyUser(user?.id?.toString());
      if (userExist) {
        await addSubNetwork(user.id);
        window.open(urlOAMatch(), '_blank');
        updateViewOAMatchModal('accepted');
        setLoadingOAMatch(false);
        setOpenDialogOAMatch(false);
        return;
      }

      const resp = await signupToOAMatch(user);
      if (resp.data.addExternUser.code === 200) {
        await addSubNetwork(user.id);
        setLoadingOAMatch(false);
        setTextPoppinOAMatch({
          title: 'Votre compte est maintenant lié à Match',
          text: 'Vous pouvez dès à présent vous connecter avec vos identifiants sur la page de connexion de Match.',
          btnClose: 'Fermer',
          btnAccess: 'Se connecter à Match',
        });
        updateViewOAMatchModal('accepted');
      } else {
        setTextPoppinOAMatch(prev => ({
          ...prev,
          title: 'Un problème est survenu',
          text: 'Veuillez retentez plus tard en passant par les boutons Match.',
        }));
        setLoadingOAMatch(false);
      }
    } catch (error) {
      setTextPoppinOAMatch(prev => ({
        ...prev,
        title: 'Un problème est survenu',
        text: 'Veuillez retentez plus tard en passant par les boutons Match.',
      }));
      setLoadingOAMatch(false);
      console.error(error);
    }
  }, [addSubNetwork, signupToOAMatch, updateViewOAMatchModal, urlOAMatch, user, verifyUser]);

  const checkOAMatchIfUserHasRefused = useCallback(() => {
    if (user.statusOAMatchModal === 'refused') {
      setOpenDialogOAMatch(true);
    } else {
      window.open(urlOAMatch(), '_blank');
    }
  }, [urlOAMatch, user.statusOAMatchModal]);

  const confirmCandidature = useCallback(async () => {
    setLoadingCandidature(true);
    try {
      const resp = await apiServiceNative.post('/api/job/accept-candidature', {
        idUser: user.id,
        idCandidature: candidature.id,
      });
      if (resp.data) {
        setOpenCandidatureModal(false);
        await findCandidature();
        await checkCandidature();
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingCandidature(false);
    }
  }, [candidature?.id, checkCandidature, findCandidature, user.id]);

  const cancelAndDeleteCandidature = useCallback(async () => {
    setLoadingCandidature(true);
    try {
      await apiServiceNative.delete(`/api/candidatures/${candidature.id}`);
      setOpenCandidatureModal(false);
      setOpenDeclineCandidatureModal(false);
      setOpenRecrutementModal(false);
      await findCandidature();
    } catch (error) {
      console.error(error);
      setOpenCandidatureModal(false);
      setOpenDeclineCandidatureModal(false);
      setOpenRecrutementModal(false);
    } finally {
      setLoadingCandidature(false);
    }
  }, [candidature?.id, findCandidature]);

  const openExternalLink = useCallback(
    (url: string) => {
      if (language === GB_PREFIX) {
        setOpenEnglishModal(true);
        return;
      }
      window.open(url, '_blank');
    },
    [language],
  );

  const openMatch = checkOAMatchIfUserHasRefused;

  const openLifestylemetriQ = useCallback(() => undefined, []);

  const openJobOffers = useCallback(() => {
    if (showJobOffers) {
      openJobOffersAdmin();
      return;
    }
    openExternalLink('https://aiderh.softy.pro/offres');
  }, [openExternalLink, openJobOffersAdmin, showJobOffers]);

  const openCoachIA = useCallback(() => {
    const chatButton = document.querySelector('[aria-label="Chat avec conseiller"]') as HTMLElement | null;
    chatButton?.click();
  }, []);

  const goToEbookRecrutement = useCallback(() => {
    if (idEbook) {
      history.push(`/ebook-recrutement/${idEbook}/${false}/1`);
    }
  }, [history, idEbook]);

  const refreshUser = useCallback(() => {
    parseUser();
  }, []);

  return {
    user,
    language,
    disableMatch,
    canAccessOaMatch,
    showJobOffers,
    isRattached,
    serviceName,
    heroStats,
    testsToDo,
    resourceCounts,
    hasCandidature,
    idEbook,
    candidature,
    loadingCandidature,
    loadingOAMatch,
    textPoppinOAMatch,
    openPendingTestsModal,
    setOpenPendingTestsModal,
    openRecrutementModal,
    setOpenRecrutementModal,
    openCandidatureModal,
    setOpenCandidatureModal,
    openDeclineCandidatureModal,
    setOpenDeclineCandidatureModal,
    openEnglishModal,
    setOpenEnglishModal,
    openDialogOAMatch,
    setOpenDialogOAMatch,
    redirect,
    goToTest,
    logout,
    confirmCandidature,
    cancelAndDeleteCandidature,
    closeModalOA,
    closeAndRedirect,
    redirectToOAMatch,
    checkOAMatchIfUserHasRefused,
    openMatch,
    openLifestylemetriQ,
    openJobOffers,
    openCoachIA,
    goToEbookRecrutement,
    refreshUser,
  };
};

export type UseHomePageReturn = ReturnType<typeof useHomePage>;
